/**
 * The kitchen board's data: poll the change feed AS THE ENROLLED DEVICE, fold
 * responses by cursor, and hand the framework route its station and its three
 * awaited callbacks (ORDR-42).
 *
 * POLLING. The first poll is `since=0`; each later one reports the cursor on
 * screen and waits the server's `poll_after_ms`. A network failure or a 5xx
 * keeps the last board and retries, visibly. A 404 (not enrolled here) and a
 * 410 (revoked) STOP polling: neither is going to change by asking again, and
 * a revoked display that keeps polling is a revoked display still in use.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ApiClient } from "../api/client.js";
import type { KitchenRouteExtras } from "../framework/types.js";
import type { DeviceStore } from "./deviceStore.js";
import { applyFeed, lanesFrom, outcomeOf, type KdsFeed } from "./kdsFeed.js";
import { createKitchenTransitions } from "./kitchenTransitions.js";
import type { TicketState } from "./ticketStates.js";

export type KitchenStationStatus =
  | { readonly kind: "no-site" }
  | { readonly kind: "needs-device" }
  | { readonly kind: "loading" }
  | { readonly kind: "live" }
  | { readonly kind: "reconnecting"; readonly correlationId: string | null }
  | { readonly kind: "not-enrolled" }
  | { readonly kind: "revoked" };

export interface KitchenStation {
  readonly status: KitchenStationStatus;
  /** Present once the feed has answered. Absent, the route shows its fallback. */
  readonly routeProps: KitchenRouteExtras | undefined;
  readonly deviceId: string | null;
  /** Store a device id typed from `seed_pilot_venue`. False when it is not a UUID. */
  readonly enrol: (deviceId: string) => boolean;
}

export interface UseKitchenStationOptions {
  readonly client: ApiClient;
  readonly siteId: string | null;
  readonly deviceStore: DeviceStore;
  /** Injected for tests. */
  readonly clock?: () => number;
  /** Injected for tests; the server's `poll_after_ms` otherwise. */
  readonly pollAfterMs?: number;
}

const RETRY_MS = 3000;

export function useKitchenStation({
  client,
  siteId,
  deviceStore,
  clock = Date.now,
  pollAfterMs,
}: UseKitchenStationOptions): KitchenStation {
  const [deviceId, setDeviceId] = useState<string | null>(() => deviceStore.read());
  const [feed, setFeed] = useState<KdsFeed | null>(null);
  const [heardAt, setHeardAt] = useState(0);
  const [status, setStatus] = useState<KitchenStationStatus>({ kind: "loading" });
  const feedRef = useRef<KdsFeed | null>(null);

  useEffect(() => {
    if (siteId === null || deviceId === null) {
      return;
    }
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    feedRef.current = null;

    const poll = async (): Promise<void> => {
      const since = feedRef.current?.cursor ?? 0;
      const query = new URLSearchParams({ site_id: siteId, since: String(since), device_id: deviceId });
      const response = await client.request(`/ordering/kds/changes/?${query.toString()}`);
      if (!live) {
        return;
      }
      const outcome = outcomeOf(response);
      switch (outcome.kind) {
        case "feed": {
          const next = applyFeed(feedRef.current, outcome.feed);
          feedRef.current = next;
          setFeed(next);
          setHeardAt(clock());
          setStatus({ kind: "live" });
          timer = setTimeout(() => void poll(), pollAfterMs ?? next.pollAfterMs);
          return;
        }
        case "unchanged":
          setHeardAt(clock());
          setStatus({ kind: "live" });
          timer = setTimeout(() => void poll(), pollAfterMs ?? feedRef.current?.pollAfterMs ?? RETRY_MS);
          return;
        case "not-enrolled":
          setStatus({ kind: "not-enrolled" });
          return;
        case "revoked":
          setStatus({ kind: "revoked" });
          return;
        case "failed":
          setStatus({ kind: "reconnecting", correlationId: outcome.correlationId });
          timer = setTimeout(() => void poll(), pollAfterMs ?? RETRY_MS);
          return;
      }
    };

    void poll();
    return () => {
      live = false;
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [client, siteId, deviceId, clock, pollAfterMs]);

  // Read from the rendered feed, so a bump always acts on the state the cook
  // was looking at when they tapped.
  const serverStateOf = useCallback(
    (ticketId: string): TicketState | undefined =>
      feed?.tickets.find((entry) => entry.ticket.id === ticketId)?.serverState,
    [feed],
  );

  const transitions = useMemo(
    () => (deviceId === null ? null : createKitchenTransitions({ client, deviceId, serverStateOf })),
    [client, deviceId, serverStateOf],
  );

  const routeProps = useMemo<KitchenRouteExtras | undefined>(() => {
    if (feed === null || transitions === null || deviceId === null) {
      return undefined;
    }
    return {
      station: { stationId: deviceId, view: "ticket", lanes: lanesFrom(feed, heardAt) },
      onBump: transitions.onBump,
      onItemBump: transitions.onItemBump,
      onRecall: transitions.onRecall,
    };
  }, [feed, heardAt, transitions, deviceId]);

  const enrol = useCallback(
    (next: string): boolean => {
      if (!deviceStore.write(next)) {
        return false;
      }
      setFeed(null);
      setStatus({ kind: "loading" });
      setDeviceId(deviceStore.read());
      return true;
    },
    [deviceStore],
  );

  const effectiveStatus: KitchenStationStatus =
    siteId === null ? { kind: "no-site" } : deviceId === null ? { kind: "needs-device" } : status;

  return { status: effectiveStatus, routeProps, deviceId, enrol };
}
